import { EPropertySettingTypes } from 'api/meta/enums/nodes/properties/EPropertySettingTypes';
import type { PropertySettings } from 'state/selectedTemplate/selectedTemplateStore';
import { EPropertyEditTypes } from 'state/selectedTemplate/selectedTemplateStore';
import { isValidPropertyDate } from 'utils/StringFormatter';
import { getUtf8ByteLength } from 'utils/strings/getUtf8ByteLength';
import type { Nullable } from 'utils/values';

import { getPropertyEditType } from './getPropertyEditType';

/** Matches the backend's limit on a single string property value. */
export const TEXT_BYTES_LIMIT = 8000;

export const PROPERTY_VALIDATION = {
  TEXT_TOO_LONG: "Text too long. Changes won't be saved.",
  /** From a `type='number'` input's own validity when the value is empty — not from the string. */
  NOT_A_NUMBER: "Not a valid number. Changes won't be saved.",
  NOT_INTEGER: "Must be a whole number. Changes won't be saved.",
  NUMBER_OUT_OF_RANGE: "Number is too large. Changes won't be saved.",
  NOT_A_DATE: "Not a valid date. Changes won't be saved."
} as const;

/**
 * What the backend accepts for a Long property: `PropertyValueCoercer.TryCoerceString` parses it with
 * `long.TryParse(value, CultureInfo.InvariantCulture, out _)`, i.e. `NumberStyles.Integer` — a sign
 * and digits, nothing else. `Number.isInteger` is a weaker test that also passes `1.0` and `1e5`,
 * both of which the save then fails on.
 */
const WHOLE_NUMBER = /^[+-]?\d+$/;

export interface ValidatePropertyValueOptions {
  /** From a `type='number'` input's `ValidityState.badInput` — not derivable from `value`. */
  badInput?: boolean;
}

/**
 * Validates an edited property value against its template settings. Pure — returns the message to
 * show, or `null` when the value is fine, matching the house style of `validateShapeName` and
 * `useValidations`.
 *
 * An empty value is always valid: clearing a property is a legitimate edit, persisted as `null` by
 * `normalizePropertyValue`.
 */
export function validatePropertyValue(
  value: string,
  settings: Nullable<PropertySettings>,
  options?: ValidatePropertyValueOptions
): Nullable<string> {
  if (options?.badInput) {
    return PROPERTY_VALIDATION.NOT_A_NUMBER;
  }

  if (value.trim() === '') {
    return null;
  }

  switch (getPropertyEditType(settings)) {
    case EPropertyEditTypes.Text:
      return getUtf8ByteLength(value) > TEXT_BYTES_LIMIT ? PROPERTY_VALIDATION.TEXT_TOO_LONG : null;
    case EPropertyEditTypes.Number:
      // Rational shares this edit type and has no shape constraint of its own; Integer does. Neither
      // needs a "is this a number at all" check: the value reaching here came out of a `type='number'`
      // input, which reports anything it could not parse as empty rather than as text — see
      // `usePropertyValueEditor`, which turns that into NOT_A_NUMBER.
      return settings?.$type === EPropertySettingTypes.Integer ? validateWholeNumber(value) : null;
    case EPropertyEditTypes.Date:
      return isValidPropertyDate(value) ? null : PROPERTY_VALIDATION.NOT_A_DATE;
    default:
      // Picklist, Ownership and AutoId are driven by a widget that cannot produce a bad value, and
      // Unknown is rendered read-only.
      return null;
  }
}

function validateWholeNumber(value: string): Nullable<string> {
  const trimmed = value.trim();

  if (!WHOLE_NUMBER.test(trimmed)) {
    return PROPERTY_VALIDATION.NOT_INTEGER;
  }

  // Int64 reaches further than the double every value in this app is carried in, so a longer whole
  // number cannot be shown, compared or sent back without having already been rounded — `JSON.parse`
  // would have done it on the way down. The safe-integer range is therefore the honest limit to
  // report, even though the backend column itself would hold more.
  return Number.isSafeInteger(Number(trimmed)) ? null : PROPERTY_VALIDATION.NUMBER_OUT_OF_RANGE;
}
